import React from 'react';
import useProjectStore from '../../ProjectCanvas/useProjectStore';

// MUI
import { styled } from '@mui/system';
import {
  Accordion,
  AccordionSummary,
  Button,
  Grid,
  Tooltip,
  Box,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
  Checkbox,
} from '@mui/material';
import { format } from '../../../utils/Date';
import { Link } from 'react-router-dom';
import { keyframes } from '@emotion/react';

// Icons
import StartIcon from '@mui/icons-material/Start';
import EventIcon from '@mui/icons-material/Event';
import { OpenInNew, Fingerprint, Code } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CachedIcon from '@mui/icons-material/Cached';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';

// Util
import config from '../../../config';

// Animations
const fadeIn = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
});

const slideIn = keyframes({
  from: {
    opacity: 0,
    transform: 'translateY(-4px)',
  },
  to: {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

// Styled components
const FlexBox = styled(Box)(({ align, justify, flex, wrap = true }) => ({
  display: 'flex',
  alignItems: align || 'center',
  justifyContent: justify || 'flex-start',
  flex: flex,
  gap: '6px',
  flexWrap: wrap ? 'wrap' : 'nowrap',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  fontWeight: 500,
  fontSize: '12px',
  padding: '4px 8px',
  borderRadius: '4px',
  border: `1px solid ${theme.palette.divider}`,
  height: '28px',
  minWidth: '70px',
  transition: 'all 0.2s ease',
  boxShadow: 'none',
  margin: '2px 0',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: 'none',
  },
  '&:active': {
    backgroundColor: theme.palette.action.selected,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '14px',
    opacity: 0.8,
    marginRight: '4px',
  },
}));

const MetaText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  margin: '2px 0',
}));

const IdChip = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  fontFamily: 'monospace',
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  backgroundColor: theme.palette.action.hover,
  borderRadius: '4px',
  padding: '2px 6px',
  border: `1px solid ${theme.palette.divider}`,
  whiteSpace: 'nowrap',
  justifyContent: 'center',
  margin: '2px 0',
  flexBasis: 'auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '280px',
  minWidth: 0,
  flexShrink: 1,
}));

const VersionChip = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  backgroundColor: theme.palette.action.hover,
  borderRadius: '4px',
  padding: '2px 6px',
  border: `1px solid ${theme.palette.divider}`,
  whiteSpace: 'nowrap',
  justifyContent: 'center',
  minWidth: '36px',
  margin: '2px 0',
  flexBasis: 'auto',
}));

const StyledAccordion = styled(Accordion)(({ selected, theme }) => ({
  backgroundColor: selected ? theme.palette.action.selected : theme.palette.background.paper,
  padding: '8px 12px',
  width: '100%',
  borderRadius: '6px',
  boxShadow: `0 1px 2px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.06)'}`,
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  transition: 'all 0.2s ease',
  marginBottom: '6px',
  animation: `${fadeIn} 0.3s ease-in-out`,
  '&:before': {
    display: 'none',
  },
  '&:hover': {
    cursor: !selected && 'pointer',
    backgroundColor: !selected ? theme.palette.action.hover : theme.palette.action.selected,
    borderColor: !selected ? theme.palette.divider : theme.palette.primary.main,
  },
}));

const StyledSummary = styled(AccordionSummary)({
  padding: 0,
  minHeight: 'unset',
  '& .MuiAccordionSummary-content': {
    margin: 0,
    width: '100%',
  },
});

const SearchHitBox = styled(Box)(({ theme }) => ({
  marginTop: '8px',
  padding: '8px',
  backgroundColor: theme.palette.background.subtle,
  borderRadius: '4px',
  border: `1px solid ${theme.palette.divider}`,
  fontSize: '12px',
  color: theme.palette.text.secondary,
  animation: `${slideIn} 0.3s ease-in-out`,
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  width: '100%',
  overflow: 'hidden',
}));

const highlightColors = [
  'rgba(255, 255, 0, 0.6)', // bright yellow
  'rgba(50, 205, 50, 0.4)', // lime green
  'rgba(255, 105, 180, 0.4)', // hot pink
  'rgba(30, 144, 255, 0.4)', // dodger blue
  'rgba(255, 165, 0, 0.4)', // orange
  'rgba(147, 112, 219, 0.4)', // medium purple
  'rgba(0, 206, 209, 0.4)', // turquoise
  'rgba(255, 99, 71, 0.4)', // tomato red
  'rgba(152, 251, 152, 0.4)', // pale green
  'rgba(238, 130, 238, 0.4)', // violet
  'rgba(135, 206, 235, 0.4)', // sky blue
  'rgba(255, 218, 185, 0.6)', // peach
];

const StyledMark = styled('mark')(({ colorIndex }) => ({
  backgroundColor: highlightColors[colorIndex % highlightColors.length],
  padding: '1px 2px',
  borderRadius: '2px',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  maxWidth: '100%',
}));

const StyledIcon = styled(({ component: Component, ...props }) => <Component {...props} />)(
  ({ theme }) => ({
    fontSize: '14px',
    opacity: 0.7,
    color: theme.palette.text.secondary,
  })
);

const formatSearchHit = searchHit => {
  if (!searchHit) return '';

  const parts = searchHit.split(/<\/?mark>/g);

  let markCount = 0;
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <StyledMark key={index} colorIndex={markCount++}>
        {part}
      </StyledMark>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );
};

const CustomStatusChip = ({ status, label }) => {
  const theme = useTheme();
  let bgColor = theme.palette.action.hover;
  let textColor = theme.palette.text.primary;
  let borderColor = theme.palette.divider;

  switch (status) {
    case 'SUCCESS':
      bgColor = 'rgba(46, 125, 50, 0.1)';
      textColor = 'rgb(46, 125, 50)';
      borderColor = 'rgba(46, 125, 50, 0.2)';
      break;
    case 'FAIL':
      bgColor = 'rgba(176, 0, 32, 0.1)';
      textColor = 'rgb(176, 0, 32)';
      borderColor = 'rgba(176, 0, 32, 0.2)';
      break;
    case 'INPROGRESS':
      bgColor = 'rgba(33, 150, 243, 0.1)';
      textColor = 'rgb(33, 150, 243)';
      borderColor = 'rgba(33, 150, 243, 0.2)';
      break;
    case 'WAITING':
      bgColor = 'rgba(81, 92, 189, 0.1)';
      textColor = '#515CBD';
      borderColor = 'rgba(81, 92, 189, 0.2)';
      break;
    case 'PAUSED':
      bgColor = 'rgba(245, 124, 0, 0.1)';
      textColor = 'rgb(245, 124, 0)';
      borderColor = 'rgba(245, 124, 0, 0.2)';
      break;
    case 'TERMINATED':
      bgColor = 'rgba(97, 97, 97, 0.1)';
      textColor = 'rgb(97, 97, 97)';
      borderColor = 'rgba(97, 97, 97, 0.2)';
      break;
  }

  const renderStatusIcon = () => {
    switch (status) {
      case 'FAIL':
        return <ErrorOutlineIcon style={{ color: textColor, fontSize: '16px' }} />;
      case 'PAUSED':
        return <PauseCircleOutlineIcon style={{ color: textColor, fontSize: '16px' }} />;
      case 'SUCCESS':
      case 'SUCCESS_END':
        return <CheckCircleOutlineIcon style={{ color: textColor, fontSize: '16px' }} />;
      case 'INPROGRESS':
        return <CachedIcon style={{ color: textColor, fontSize: '16px' }} />;
      case 'WAITING':
        return <PendingOutlinedIcon style={{ color: textColor, fontSize: '16px' }} />;
      case 'TERMINATED':
        return <RemoveCircleOutlineIcon style={{ color: textColor, fontSize: '16px' }} />;
      default:
        return <PendingOutlinedIcon style={{ color: textColor, fontSize: '16px' }} />;
    }
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '22px',
        backgroundColor: bgColor,
        color: textColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 500,
        padding: '0 8px',
        minWidth: '75px',
        margin: '2px 0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', marginRight: '4px' }}>
        {renderStatusIcon()}
      </Box>
      <Box>{label}</Box>
    </Box>
  );
};

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: '2px',
  color: theme.palette.text.secondary,
  '&.Mui-checked': {
    color: theme.palette.text.primary,
  },
}));

function SessionLogBox({
  row,
  index,
  classes,
  renderIcon,
  renderStatus,
  displayTestData,
  handleToggleOne,
  expanded,
  displayUnsubmittedData,
  selectedSession,
  setSelectedSession,
  selectMode = false,
  selectedSessions = [],
  onSelectSession,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const isSelected = selectedSessions.includes(row.projectSessionId);

  const handleOpenInNew = e => {
    if (e.target.id === 'view-progress-btn' || e.target.id === 'session-checkbox') return;

    // If in select mode, handle selection instead of opening
    if (selectMode) {
      onSelectSession(row.projectSessionId);
      return;
    }

    // If it's displaying the unsubmitted form data
    // open the URL in a new tab
    if (displayUnsubmittedData) {
      const url = config.buildFormUrl(`/form/${row.formId}/${row.projectSessionId}`);
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    // Else continue to setting the selected session
    if (selectedSession && selectedSession.projectSessionId === row.projectSessionId)
      setSelectedSession(null);
    else setSelectedSession(row);
  };

  const handleViewProgress = e => {
    if (e.target.id === 'open-in-new-btn' || e.target.id === 'session-checkbox') return;

    // If in select mode, handle selection instead of view progress
    if (selectMode) {
      onSelectSession(row.projectSessionId);
      return;
    }

    if (selectedSession && selectedSession.projectSessionId === row.projectSessionId)
      setSelectedSession(null);
    else setSelectedSession(row);
  };

  const handleCheckboxClick = e => {
    e.stopPropagation();
    onSelectSession(row.projectSessionId);
  };

  const getStatusColor = status => {
    switch (status) {
      case 'SUCCESS':
      case 'SUCCESS_END':
        return 'rgb(46, 125, 50)';
      case 'FAIL':
        return 'rgb(176, 0, 32)';
      case 'INPROGRESS':
        return 'rgb(33, 150, 243)';
      case 'PAUSED':
      case 'WAITING':
        return 'rgb(245, 124, 0)';
      case 'TERMINATED':
        return 'rgb(97, 97, 97)';
      default:
        return theme.palette.text.primary;
    }
  };

  const getStatusChip = status => {
    return (
      <CustomStatusChip
        status={status}
        label={status === 'FAIL' ? 'Error' : renderStatus(displayUnsubmittedData, status, true)}
      />
    );
  };

  const renderSessionContent = () => {
    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
        {selectMode && (
          <Box sx={{ mr: 1, flexShrink: 0 }}>
            <StyledCheckbox
              id="session-checkbox"
              checked={isSelected}
              onClick={handleCheckboxClick}
              size="small"
            />
          </Box>
        )}
        <Box sx={{ width: '100%' }}>
          <FlexBox
            justify="flex-start"
            sx={{
              gap: '8px',
              rowGap: '4px',
              width: '100%',
            }}
          >
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  flexBasis: { xs: '100%', sm: 'auto' },
                  mb: { xs: 1, sm: 0 },
                }}
              >
                {getStatusChip(row.status)}
              </Box>
            )}

            <IdChip sx={{ flexBasis: 'auto' }}>
              <StyledIcon component={Fingerprint} />
              {row.projectSessionId}
            </IdChip>

            <VersionChip sx={{ flexBasis: 'auto' }}>
              <StyledIcon component={Code} />v{row.projectVersion}
            </VersionChip>

            {!displayUnsubmittedData && !isMobile && (
              <Tooltip title={format(row.createdAt, useProjectStore.getState().timezone, 'full')}>
                <MetaText>
                  <StyledIcon component={StartIcon} />
                  {row.createdAt ? format(row.createdAt, useProjectStore.getState().timezone) : 'N/A'}
                </MetaText>
              </Tooltip>
            )}

            <Tooltip title={format(row.lastUpdated, useProjectStore.getState().timezone, 'full')}>
              <MetaText>
                <StyledIcon component={EventIcon} />
                {row.lastUpdated
                  ? isMobile
                    ? format(row.lastUpdated, useProjectStore.getState().timezone, 'dateTime')
                        .split(' ')
                        .slice(0, 3)
                        .join(' ')
                    : format(row.lastUpdated, useProjectStore.getState().timezone)
                  : 'N/A'}
              </MetaText>
            </Tooltip>

            {displayUnsubmittedData && !selectMode && (
              <ActionButton
                variant="outlined"
                size="small"
                id="open-in-new-btn"
                onClick={handleOpenInNew}
                startIcon={<OpenInNew />}
                sx={{ ml: 'auto', flexShrink: 0 }}
              >
                Open
              </ActionButton>
            )}
          </FlexBox>

          {row?.searchHit && <SearchHitBox>{formatSearchHit(row.searchHit)}</SearchHitBox>}
        </Box>
      </Box>
    );
  };

  return (
    <StyledAccordion
      expanded={expanded[row.projectSessionId]}
      selected={selectedSession && selectedSession.projectSessionId === row.projectSessionId}
    >
      <StyledSummary
        onClick={displayUnsubmittedData ? handleOpenInNew : handleViewProgress}
        id={`session-log-box`}
      >
        {renderSessionContent()}
      </StyledSummary>
    </StyledAccordion>
  );
}

export default SessionLogBox;
